import _, {chain} from 'lodash';

import {
  ApplicationConversationStatus,
  ApplicationInboxId,
  ApplicationTagId,
  ApplicationTeammateId,
} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {isTeammateActive} from '../../../helpers/modelHelpers';
import {
  canMoveConversationIdsToInbox,
  ConversationActionContextFinder,
  ConversationInboxIdsFinder,
  ConversationNamespaceFinder,
  ConversationStatusesEnum,
} from '../../../helpers/models/conversationHelpers';
import {tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {ViewerPanelId} from '../../panels/panelStateBase';
import {makeGetContentPage, makeGetViewerPanel} from '../../panels/viewer/viewerPanelSelectors';
import {
  assignToTeammateAction,
  canAssignToTeammateAction,
} from '../conversations/assigneeConversationsActions';
import {
  canUserMoveToInboxAction,
  moveToInboxAction,
  MoveToInboxSourceTypesEnum,
} from '../conversations/moveConversationsActions';
import {setConversationsStatusAction} from '../conversations/statusConversationsActions';
import {addAndRemoveTagsFromConversationsAction} from '../conversations/tagConversationsActions';
import {fetchInboxByIdAsyncAction} from '../inboxes/inboxesActions';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {getUserId} from '../teammates/teammatesSelectors';

/*
 * Actions.
 */

export function applicationAssignConversationsAction(
  conversationIds: ReadonlyArray<number>,
  teammatePublicId: ApplicationTeammateId | null,
  findConversationInboxIds: ConversationInboxIdsFinder,
): ThunkAction {
  return (dispatch, getState) => {
    if (!teammatePublicId) {
      dispatch(assignToTeammateAction(conversationIds, undefined));
      return;
    }

    const teammateId = tryParsePublicId(teammatePublicId);
    const findTeammate = getFindTeammate(getState());
    const teammate = teammateId && findTeammate(teammateId, 'applicationAssignConversationsAction');
    if (!teammate || !isTeammateActive(teammate)) {
      throw new Error(`Teammate ${teammatePublicId} not found.`);
    }

    const canAssignToTeammate = dispatch(
      canAssignToTeammateAction(conversationIds, teammate, findConversationInboxIds),
    );
    if (!canAssignToTeammate) {
      throw new Error(`Teammate ${teammatePublicId} cannot be assigned to the conversations.`);
    }

    dispatch(assignToTeammateAction(conversationIds, teammate.id));
  };
}

export function applicationMoveConversationsAction(
  conversationIds: ReadonlyArray<number>,
  inboxPublicId: ApplicationInboxId,
  findConversationNamespace: ConversationNamespaceFinder,
  shouldRetainAccess: boolean | undefined,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Make sure the inbox exists and is valid target for the teammate.
    const inboxId = tryParsePublicId(inboxPublicId);
    const inbox = inboxId && (await dispatch(fetchInboxByIdAsyncAction(inboxId)));
    if (!inbox) {
      throw new Error(`Inbox ${inboxPublicId} not found.`);
    }

    // Make the operation is permitted.
    const canMoveToInbox = dispatch(canUserMoveToInboxAction(inbox));
    const canMoveConversations = canMoveConversationIdsToInbox(
      conversationIds,
      inbox,
      findConversationNamespace,
    );
    if ((!canMoveToInbox || !canMoveConversations) && !_.isBoolean(shouldRetainAccess)) {
      throw new Error(`The conversations cannot be moved to the inbox ${inboxPublicId}.`);
    }

    dispatch(
      moveToInboxAction(
        getUserId(getState()),
        conversationIds,
        inbox.id,
        {
          type: MoveToInboxSourceTypesEnum.PLUGIN_API,
        },
        shouldRetainAccess,
      ),
    );
  };
}

export const applicationConversationStatusMap: {
  [key in ApplicationConversationStatus]: ConversationStatusesEnum;
} = {
  open: ConversationStatusesEnum.OPEN,
  archived: ConversationStatusesEnum.ARCHIVED,
  trashed: ConversationStatusesEnum.TRASHED,
  spam: ConversationStatusesEnum.SPAM,
};

export function applicationSetConversationsStatusAsyncAction(
  panelId: ViewerPanelId,
  conversationIds: ReadonlyArray<number>,
  status: ApplicationConversationStatus,
  findConversationActionContext: ConversationActionContextFinder,
): ThunkAsyncAction {
  const getViewerPanel = makeGetViewerPanel<void>(() => panelId);
  const getViewerPage = makeGetContentPage(getViewerPanel);

  return async (dispatch, getState) => {
    const {cellsContext} = getViewerPage(getState());
    const newStatus = applicationConversationStatusMap[status];

    return dispatch(
      setConversationsStatusAction(cellsContext, conversationIds, newStatus, findConversationActionContext),
    );
  };
}

export function applicationTagConversationsAsyncAction(
  conversationIds: ReadonlyArray<number>,
  tagPublicIds: ReadonlyArray<ApplicationTagId>,
): ThunkAction {
  const tagIds = chain(tagPublicIds)
    .map((id) => tryParsePublicId(id))
    .compact()
    .value();

  return addAndRemoveTagsFromConversationsAction(conversationIds, {add: tagIds});
}

export function applicationUntagConversationsAsyncAction(
  conversationIds: ReadonlyArray<number>,
  tagPublicIds: ReadonlyArray<ApplicationTagId>,
): ThunkAction {
  const tagIds = _(tagPublicIds)
    .map((tagId) => tryParsePublicId(tagId))
    .compact()
    .value();

  return addAndRemoveTagsFromConversationsAction(conversationIds, {remove: tagIds});
}
